import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as companyTypeApi from '../api/companyTypeApi';
import type { CompanyTypeItemModel } from '../types/api';

// Reducer shape allows id as string | number for compatibility with list responses
interface CompanyType {
  id: string | number;
  name: string;
  isDefault: boolean;
  companies: number;
  userProfiles: number;
  [key: string]: unknown;
}

type CompanyTypesState = CompanyType[] | null;

export const getAllCompanyTypes = createAsyncThunk<CompanyType[], void>(
  'companyTypes/fetchCompanyTypes',
  async (_, { dispatch }) => {
    const companyTypes = (await companyTypeApi.getAllCompanyTypes()) as CompanyType[];
    dispatch(loadCompanyTypes(companyTypes));
    return companyTypes;
  }
);

export const saveCompanyType = createAsyncThunk<CompanyType, CompanyType>(
  'companyTypes/saveCompanyType',
  async (companyType, { dispatch }) => {
    const payload: Partial<CompanyTypeItemModel> = {
      ...companyType,
      id: companyType.id != null ? String(companyType.id) : undefined,
    };
    const { id } = await companyTypeApi.saveCompanyType(payload);

    const merged: CompanyType = { ...companyType, id };
    if (companyType.id) {
      dispatch(updateCompanyType(merged));
    } else {
      dispatch(createCompanyType(merged));
    }

    return merged;
  }
);

export const deleteCompanyType = createAsyncThunk<void, string>(
  'companyTypes/deleteCompanyType',
  async (id, { dispatch }) => {
    await companyTypeApi.deleteCompanyType(id);
    dispatch(companyTypesSlice.actions.deleteCompanyTypeAction(id));
  }
);

const companyTypesSlice = createSlice({
  name: 'companyTypes',
  initialState: null as CompanyTypesState,
  reducers: {
    loadCompanyTypes: (state, action: PayloadAction<CompanyType[]>) => {
      return action.payload;
    },
    updateCompanyType: (state, action: PayloadAction<CompanyType>) => {
      if (state === null) return state;

      return state.map((companyType) =>
        companyType.id === action.payload.id ? action.payload : companyType
      );
    },
    createCompanyType: (state, action: PayloadAction<CompanyType>) => {
      if (state === null) return state;

      const exists = state.find((x) => x.id === action.payload.id);

      if (exists) {
        // Update existing company type
        return state.map((companyType) =>
          companyType.id === action.payload.id ? action.payload : companyType
        );
      } else {
        // Create new company type
        return [...state, action.payload];
      }
    },
    deleteCompanyTypeAction: (
      state,
      action: PayloadAction<string | number>
    ) => {
      if (state === null) return state;

      return state.filter((companyType) => companyType.id !== action.payload);
    },
  },
});

export const {
  loadCompanyTypes,
  updateCompanyType,
  createCompanyType,
  deleteCompanyTypeAction,
} = companyTypesSlice.actions;

export default companyTypesSlice.reducer;
